import { DetailedHTMLProps, HTMLAttributes, memo, ReactNode, useState } from 'react'

import { cn } from '../../lib/utils'

export type FlipCardProps = DetailedHTMLProps<HTMLAttributes<HTMLElement>, HTMLElement> & {
  back: string
  front: string
  children?: ReactNode
  examples?: string
  innerContainerClassName?: string
  pronunciation?: string
}

export const highlightWord = (sentence: string, words: string[]) =>
  sentence.replace(
    new RegExp(`(${words.join('|')})`, 'gi'),
    '<strong class="font-semibold text-slate-800 dark:text-slate-100">$1</strong>',
  )

export const FlipCard = memo(
  ({
    front,
    back,
    examples,
    children,
    className,
    onClick,
    innerContainerClassName,
    pronunciation,
    ref,
    ...articleProps
  }: FlipCardProps) => {
    const [isFront, setIsFront] = useState(true)

    return (
      <article
        ref={ref}
        className={cn(
          'sm-landscape:max-h-none group relative mx-auto h-full max-h-[50vh] w-[90vw] max-w-md [perspective:1300px]',
          className,
        )}
        onClick={e => {
          onClick?.(e)
          if (e.defaultPrevented) return
          setIsFront(!isFront)
        }}
        {...articleProps}
      >
        <div
          className={cn(
            'relative h-full w-full rounded-3xl border border-slate-100 shadow-sm transition-transform duration-1000 ease-in-out [transform-style:preserve-3d] dark:border-gray-700',
            isFront || '[transform:rotateY(180deg)]',
            innerContainerClassName,
          )}
        >
          <h3 className="absolute inset-0 flex h-full w-full [transform:translateZ(1px)] items-center justify-center rounded-3xl border border-white/35 bg-gradient-to-br from-slate-50 via-white to-slate-100 px-6 py-8 text-center text-3xl leading-snug font-semibold tracking-tight text-balance [overflow-wrap:anywhere] text-slate-800 antialiased shadow-inner shadow-slate-300/25 [backface-visibility:hidden] dark:border-white/5 dark:from-slate-800 dark:via-slate-900 dark:to-slate-950 dark:text-slate-100 dark:shadow-black/25">
            {front}
          </h3>
          <div className="absolute inset-0 h-full w-full [transform:rotateY(180deg)_translateZ(1px)] rounded-3xl border border-white/25 bg-gradient-to-br from-slate-50 via-slate-100 to-slate-200 text-slate-800 antialiased shadow-inner shadow-slate-300/20 [backface-visibility:hidden] dark:border-white/5 dark:from-slate-800 dark:via-slate-900 dark:to-slate-950 dark:text-slate-200 dark:shadow-black/25">
            <div className="flex h-full min-h-0 flex-col items-center justify-center overflow-y-auto px-5 py-6 sm:px-6 sm:py-7">
              <div className="sm-landscape:flex-row flex w-full max-w-prose flex-col flex-wrap items-center gap-4 text-center">
                <h3 className="max-w-full text-3xl leading-snug font-semibold tracking-tight text-pretty break-words text-slate-800 dark:text-slate-100">
                  {back}
                </h3>
                {pronunciation && (
                  <p className="max-w-full text-lg leading-normal font-normal tracking-wide text-pretty break-words text-slate-500 dark:text-slate-400">
                    {pronunciation}
                  </p>
                )}
                {examples && (
                  <p
                    dangerouslySetInnerHTML={{
                      __html: highlightWord(examples, [back, front]),
                    }}
                    className="w-full text-left text-base leading-relaxed text-slate-600 dark:text-slate-400"
                  />
                )}
              </div>
            </div>
          </div>
          {children ? (
            <div className="pointer-events-none absolute inset-0 [transform:translateZ(2px)] [backface-visibility:visible]">
              {children}
            </div>
          ) : null}
        </div>
      </article>
    )
  },
)

FlipCard.displayName = 'FlipCard'
